import type { AgentView, Status } from "./contracts";
import { PRESENCE_TTL_MS } from "./contracts";

type PresenceSubject = Pick<AgentView, "status" | "lastSeen" | "evidence"> &
  Partial<Pick<AgentView, "intent">>;

export function serverNow(
  serverTime: number,
  receivedAt: number | null,
  localNow: number,
): number {
  if (!Number.isFinite(serverTime) || receivedAt === null) return localNow;
  if (localNow < receivedAt) return serverTime + PRESENCE_TTL_MS;
  return serverTime + Math.max(0, localNow - receivedAt);
}

export function effectiveStatus(
  agent: PresenceSubject,
  now: number,
  disconnected = false,
): Status {
  if (disconnected) return "unknown";
  const evidence = agent.evidence;
  if (evidence?.health === "restricted") return "offline";
  if (agent.lastSeen === null)
    return agent.status === "offline" ? "offline" : "unknown";
  if (!Number.isFinite(agent.lastSeen) || agent.lastSeen > now + 5_000)
    return "unknown";
  if (now - agent.lastSeen >= PRESENCE_TTL_MS) return "offline";
  if (evidence) {
    if (evidence.health !== "fresh")
      return agent.status === "offline" ? "offline" : "unknown";
    if (
      evidence.validUntil === null ||
      !Number.isFinite(evidence.validUntil) ||
      now >= evidence.validUntil
    )
      return "unknown";
    if (evidence.observedState === "busy") return "busy";
    if (evidence.observedState === "ended") return "wrap_up";
    if (evidence.observedState !== "idle") return "unknown";
  }
  if (
    agent.status === "available" &&
    "intent" in agent &&
    agent.intent !== "ready"
  )
    return agent.intent ?? "unknown";
  return agent.status;
}

export function presenceDescription(
  agent: PresenceSubject,
  now: number,
  disconnected = false,
): string {
  if (disconnected) return "Service disconnected · activity unverified";
  const evidence = agent.evidence;
  if (evidence?.reason === "inactive") return "Account inactive";
  if (evidence?.reason === "pilot_disabled") return "Pilot access is off";
  if (agent.lastSeen === null || evidence?.reason === "no_observation")
    return "No GHL observation yet";
  if (earlierSessionUnverified(agent))
    return "Earlier device session unverified · reconnect that device or ask an admin to review it";
  if (evidence?.reason === "expired" || now - agent.lastSeen >= PRESENCE_TTL_MS)
    return "GHL observation expired · waiting for an update";
  if (evidence?.reason === "unapproved_location")
    return "GHL location is not approved";
  if (evidence?.reason === "call_continued")
    return evidence.observedState === "ended"
      ? "Wrap-up retained · waiting for the call tab to update"
      : "Call session retained · waiting for the call tab to update";
  if (
    evidence?.reason === "unreadable" ||
    evidence?.observedState === "unknown"
  )
    return evidence.tabCount > 1
      ? "Call state unverified · check your open GHL tabs"
      : "Call state unverified · check the GHL call panel";
  if (evidence && effectiveStatus(agent, now) === "unknown")
    return "Observation needs an update";
  const seconds = Math.max(0, Math.floor((now - agent.lastSeen) / 1_000));
  const elapsed = seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m`;
  return `GHL observed ${elapsed} ago${evidence && evidence.tabCount > 1 ? ` · ${evidence.tabCount} tabs` : ""}`;
}

export function earlierSessionUnverified(agent: PresenceSubject): boolean {
  return (
    agent.status === "unknown" &&
    agent.evidence?.health === "stale" &&
    agent.evidence.observedState === "unknown"
  );
}

export function statusDuration(since: number, now: number): string {
  const seconds = Math.max(0, Math.floor((now - since) / 1_000));
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3_600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  return `${Math.floor(seconds / 3_600)}h ${Math.floor((seconds % 3_600) / 60)}m`;
}
